import parse from 'date-fns/parse';
import lscache from 'lscache';
import { Account } from 'model/IChat';
import { ExperienceSource } from 'model/IExperience';
import React, { useMemo } from 'react';
import HostSuccessSection from 'reusable-components/forms/ContactHostForm/ContactHostSuccess/HostSuccessSection';
import OtherLocalsSuccessSectionContainer from 'reusable-components/forms/ContactHostForm/OtherLocalsSuccessSection';
import OtherLocalsSuccessByDurationSectionContainer from 'reusable-components/forms/ContactHostForm/OtherLocalsSuccessSection/OtherLocalsSuccessByDurationSectionContainer';
import useHostAvailabilityByDuration from 'reusable-components/forms/ContactHostForm/RequestPersonalizedOfferForm/hooks/useHostAvailabilityByDuration';
import { Trek } from 'utils/analytics';
import convertTo24HourFormatObject from 'utils/timeHelpers/convertTo24HourFormatObject';
import { CONTACT_HOST_SOURCES } from '~/constants';
import Section from '~/reusable-components/layout/Section';

interface ContactHostSuccessProps {
  host?: Account;
  experience?: ExperienceSource;
  source: CONTACT_HOST_SOURCES;
}

function getTimeFormatted(formValues) {
  if (!formValues?.time) {
    return null;
  }
  const timeObject = convertTo24HourFormatObject(formValues.time);
  return timeObject.hour + ':' + timeObject.minute;
}

const showOtherLocalSection = (
  currentHostIsAvailable: boolean | undefined,
  experience: ExperienceSource | undefined,
  time: string | null | undefined,
  source: CONTACT_HOST_SOURCES,
) => {
  // if coming from host page, only show other hosts section when current host is not available
  // @todo: add check for other hosts availability for same duration
  if (source === CONTACT_HOST_SOURCES.HOST_PAGE) {
    return currentHostIsAvailable === false;
  }

  // otherwise, show other hosts section when there are other hosts available
  return !!(experience && time);
};

function ContactHostSuccess({
  host,
  experience,
  source,
}: ContactHostSuccessProps) {
  const formValues = lscache.get('personalizeOfferForm');
  const { hostIsAvailable } = useHostAvailabilityByDuration({
    accountId: host?.id as string,
    duration: (formValues?.duration
      ? formValues.duration * 60
      : experience?.durationMins) as number,
    time: formValues?.time as string,
    dateRange: {
      from: parse(formValues?.date, 'yyyy-MM-dd', new Date()),
    },
  });

  const time = getTimeFormatted(formValues);

  const trekkerContext = useMemo(
    () => ({
      component: ['ContactHostSuccess'],
      pageName: 'contactHostFormSent',
      source,
      experience,
      host,
      enabledForm: 'personalized-offer',
    }),
    [source, experience, host],
  );

  return (
    <Section as="main" isNarrow noSpace="top" data-test-id="ContactHostSuccess">
      <Trek context={trekkerContext}>
        {host && (
          <HostSuccessSection
            source={source}
            hostIsAvailable={!!hostIsAvailable}
            name={host.firstName}
            image={host.photo}
            personalTitle={host.personal_title}
            hostId={host.id}
          />
        )}
        {source === CONTACT_HOST_SOURCES.HOST_PAGE &&
          showOtherLocalSection(hostIsAvailable, experience, time, source) &&
          host?.hostLocations?.[0]?.area &&
          formValues?.duration &&
          formValues?.date &&
          time && (
            <OtherLocalsSuccessByDurationSectionContainer
              area={host.hostLocations[0].area}
              duration={formValues.duration}
              date={formValues.date}
              time={time}
              source={source}
            />
          )}
        {source !== CONTACT_HOST_SOURCES.HOST_PAGE &&
          showOtherLocalSection(hostIsAvailable, experience, time, source) &&
          experience &&
          host?.id &&
          formValues?.date &&
          time && (
            <OtherLocalsSuccessSectionContainer
              hosts={experience.hosts}
              hostId={host.id}
              experienceId={experience.id}
              date={formValues.date}
              time={time}
              source={source}
            />
          )}
      </Trek>
    </Section>
  );
}

export default ContactHostSuccess;
